import { ChangeEvent, FC, MouseEvent, useEffect, useMemo, useState } from "react"
import { RadioGroup as RG } from "react-radio-group"
import { Checkbox, Copy, FormFieldGroup, Label, StandardModal, TextInput, colorAttentionBlue50 } from "@rhumbix/rmbx_design_system_web"
import * as API from "../../api"
import { buildSetupOnlyContents, iTimecardTemplate } from "./timecard-template-utils"
import { RadioButtonWithLabel } from "../RadioGroup"
import SelectorWrapper from "../../SelectorWrapper"
import styled from "styled-components"

type SaveTimecardTemplateModalBaseProps = {
    open: boolean
    closeMethod: () => void
    templates: iTimecardTemplate[]
    onSaved: (template: iTimecardTemplate) => void
    onUpdated: (template: iTimecardTemplate) => void
}

/** Save from the create-time-card modal's own selectors, before any WorkShift exists. */
type SaveFromSetupProps = {
    source: "setup"
    projects: Array<{ id: number }>
    employees: Array<{ id: number }>
    costCodes: Array<{ id: number }>
    shiftStartTime: Date | null
    shiftEndTime: Date | null
    foreman: number
}

/** Save the full matrix of an already-saved WorkShift (entries/shift_extras/absences included). */
type SaveFromWorkShiftProps = {
    source: "workShift"
    workShiftId: number
}

export type SaveTimecardTemplateModalProps = SaveTimecardTemplateModalBaseProps & (SaveFromSetupProps | SaveFromWorkShiftProps)

type SaveMode = "new" | "update"

/**
 * Save a timecard as a reusable TimecardTemplate, either as a new template or overwriting an
 * existing one. `props.source` determines what gets captured: "setup" builds a setup-only
 * snapshot (no entries/shift_extras/absences) from the create-time-card modal's own selector
 * state, since that modal runs before any WorkShift exists -- its Projects/Workers/Cost Codes
 * are each individually toggleable, and Hours is always excluded. "workShift" captures an
 * already-saved WorkShift via its `work_shift_id`, letting the backend build `contents` directly
 * from the database -- Projects/Workers/Cost Codes are each individually toggleable here too
 * (via `include_projects`/`include_employees`/`include_cost_codes`), and Hours (entries/
 * shift_extras/absences) can be excluded via `include_hours`. Unchecking any one of Projects/
 * Workers/Cost Codes forces Hours off and disables it -- a partial setup can't be reconciled
 * against the full hours matrix, since entries reference the objects being excluded, so only the
 * remaining setup objects are saved. Whenever Hours is included, Shift Extras and Modifiers can
 * each be independently excluded (via `include_shift_extras`/`include_modifiers`); unchecking
 * Hours forces both off and disables them too, since neither applies without entries.
 */
const SaveTimecardTemplateModal: FC<SaveTimecardTemplateModalProps> = props => {
    const [mode, setMode] = useState<SaveMode>("new")
    const [name, setName] = useState("")
    const [templateToUpdate, setTemplateToUpdate] = useState<iTimecardTemplate | null>(null)
    const [includeProjects, setIncludeProjects] = useState(true)
    const [includeWorkers, setIncludeWorkers] = useState(true)
    const [includeCostCodes, setIncludeCostCodes] = useState(true)
    const [includeHours, setIncludeHours] = useState(true)
    const [includeShiftExtras, setIncludeShiftExtras] = useState(true)
    const [includeModifiers, setIncludeModifiers] = useState(true)
    const [errorMessage, setErrorMessage] = useState<string | undefined>()

    const allSetupCategoriesIncluded = includeProjects && includeWorkers && includeCostCodes
    const hoursIncluded = props.source === "workShift" && allSetupCategoriesIncluded && includeHours

    // Entries/shift_extras/absences reference the employees/cost codes/projects they came
    // from -- excluding any of those from setup leaves Hours with nothing valid to attach to.
    useEffect(() => {
        if (props.source === "workShift" && !allSetupCategoriesIncluded) {
            setIncludeHours(false)
        }
    }, [props.source, allSetupCategoriesIncluded])

    // Shift Extras and Modifiers are both part of the hours matrix -- neither applies once
    // Hours itself is excluded.
    useEffect(() => {
        if (!hoursIncluded) {
            setIncludeShiftExtras(false)
            setIncludeModifiers(false)
        }
    }, [hoursIncluded])

    const templateOptionList = useMemo(
        () =>
            props.templates.map(template => ({
                value: template.id,
                label: template.name,
                data: template,
            })),
        [props.templates]
    )

    const resetAndClose = () => {
        setMode("new")
        setName("")
        setTemplateToUpdate(null)
        setErrorMessage(undefined)
        props.closeMethod()
    }

    const onClickSave = () => {
        let contentsOrWorkShift:
            | { contents: iTimecardTemplate["contents"] }
            | {
                  work_shift_id: number
                  include_hours?: boolean
                  include_projects?: boolean
                  include_employees?: boolean
                  include_cost_codes?: boolean
                  include_shift_extras?: boolean
                  include_modifiers?: boolean
              }
        if (props.source === "workShift") {
            contentsOrWorkShift = {
                work_shift_id: props.workShiftId,
                ...(includeHours ? {} : { include_hours: false }),
                ...(includeProjects ? {} : { include_projects: false }),
                ...(includeWorkers ? {} : { include_employees: false }),
                ...(includeCostCodes ? {} : { include_cost_codes: false }),
                ...(includeShiftExtras ? {} : { include_shift_extras: false }),
                ...(includeModifiers ? {} : { include_modifiers: false }),
            }
        } else {
            if (!props.shiftStartTime || !props.shiftEndTime) return
            contentsOrWorkShift = {
                contents: buildSetupOnlyContents(
                    includeProjects ? props.projects : [],
                    includeWorkers ? props.employees : [],
                    includeCostCodes ? props.costCodes : [],
                    props.shiftStartTime,
                    props.shiftEndTime,
                    Intl.DateTimeFormat().resolvedOptions().timeZone,
                    props.foreman
                ),
            }
        }

        if (mode === "update") {
            if (!templateToUpdate) return
            API.updateTimecardTemplate(templateToUpdate.id, contentsOrWorkShift)
                .then((template: iTimecardTemplate) => {
                    props.onUpdated(template)
                    resetAndClose()
                })
                .catch(() => setErrorMessage("Could not update this template."))
            return
        }

        API.createTimecardTemplate({ name, ...contentsOrWorkShift })
            .then((template: iTimecardTemplate) => {
                props.onSaved(template)
                resetAndClose()
            })
            .catch((error: { response?: { data?: { errors?: { name?: string[] } } } }) => {
                setErrorMessage(error.response?.data?.errors?.name?.[0] ?? "Could not save this template.")
            })
    }

    const onModeRadioClick = (event: MouseEvent<HTMLInputElement>) => {
        setErrorMessage(undefined)
        setMode(event.currentTarget.value as SaveMode)
    }

    // SelectorWrapper invokes this with the {value,label,data} wrapper for the closed-box display,
    // but with the raw template (the wrapper's `data`) for each dropdown row -- handle both shapes.
    const formatTemplateOption = (option: { value?: { label?: string; name?: string } | null }) => option.value?.label ?? option.value?.name ?? ""

    return (
        <StandardModal
            id="save-timecard-template-modal"
            autoClose={false}
            open={props.open}
            closeMethod={resetAndClose}
            title={"Save Time Card Template"}
            description={
                props.source === "workShift"
                    ? "Save this timecard's full data as a reusable template, either as a new template or by updating one you already have."
                    : "Save this timecard's setup as a reusable template, either as a new template or by updating one you already have."
            }
            primaryBtnText={"Save Template"}
            primaryBtnOnClick={onClickSave}
            primaryBtnDisabled={mode === "update" ? !templateToUpdate : !name.trim()}
            primaryBtnColor={colorAttentionBlue50}
            secondaryBtnOnClick={resetAndClose}
            width={"550px"}
        >
            <RG name="timecard-template-save-mode" selectedValue={mode}>
                <ModeRow>
                    <RadioButtonWithLabel
                        id="timecard-template-save-mode-update"
                        value={"update"}
                        labelText={"Update an existing template"}
                        checked={mode === "update"}
                        disabled={props.templates.length === 0}
                        onClick={onModeRadioClick}
                    />
                    <SelectorContainer $dimmed={mode !== "update"}>
                        <SelectorWrapper
                            id="timecard-template-to-update-selector"
                            name="timecard-template-to-update-selector"
                            optionList={templateOptionList}
                            value={templateOptionList.find(option => option.value === templateToUpdate?.id)}
                            onChange={(template: unknown) => setTemplateToUpdate((template as iTimecardTemplate | null | undefined) ?? null)}
                            valueFormatter={formatTemplateOption}
                            titleFormatter={formatTemplateOption}
                            isClearable={true}
                            isMulti={false}
                            isDisabled={mode !== "update" || props.templates.length === 0}
                        />
                    </SelectorContainer>
                </ModeRow>
                <ModeRow>
                    <RadioButtonWithLabel id="timecard-template-save-mode-new" value={"new"} labelText={"Save as a new template"} checked={mode === "new"} onClick={onModeRadioClick} />
                    <NameInputContainer $dimmed={mode !== "new"}>
                        <TextInput
                            id="timecard-template-name"
                            value={name}
                            disabled={mode !== "new"}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                setErrorMessage(undefined)
                                setName(event.target.value)
                            }}
                        />
                    </NameInputContainer>
                </ModeRow>
            </RG>
            <FormFieldGroup>
                <Label>Included Data</Label>
                <IncludedDataGrid>
                    <IncludedDataOption>
                        <Checkbox checked={includeProjects} onChange={setIncludeProjects} />
                        <Copy>Projects</Copy>
                    </IncludedDataOption>
                    <IncludedDataOption>
                        <Checkbox checked={includeWorkers} onChange={setIncludeWorkers} />
                        <Copy>Workers</Copy>
                    </IncludedDataOption>
                    <IncludedDataOption>
                        <Checkbox checked={includeCostCodes} onChange={setIncludeCostCodes} />
                        <Copy>Cost Codes</Copy>
                    </IncludedDataOption>
                    <IncludedDataOption>
                        <Checkbox checked={props.source === "workShift" ? includeHours : false} disabled={props.source === "setup" || !allSetupCategoriesIncluded} onChange={setIncludeHours} />
                        <Copy>Hours</Copy>
                    </IncludedDataOption>
                    <IncludedDataOption>
                        <Checkbox checked={hoursIncluded ? includeShiftExtras : false} disabled={!hoursIncluded} onChange={setIncludeShiftExtras} />
                        <Copy>Shift Extras</Copy>
                    </IncludedDataOption>
                    <IncludedDataOption>
                        <Checkbox checked={hoursIncluded ? includeModifiers : false} disabled={!hoursIncluded} onChange={setIncludeModifiers} />
                        <Copy>Modifiers</Copy>
                    </IncludedDataOption>
                </IncludedDataGrid>
            </FormFieldGroup>
            {errorMessage && <Copy>{errorMessage}</Copy>}
        </StandardModal>
    )
}

export default SaveTimecardTemplateModal

const ModeRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 8px;
`

const SelectorContainer = styled.div<{ $dimmed: boolean }>`
    width: 260px;
    opacity: ${props => (props.$dimmed ? 0.5 : 1)};
`

const NameInputContainer = styled.div<{ $dimmed: boolean }>`
    width: 260px;
    opacity: ${props => (props.$dimmed ? 0.5 : 1)};
`

const IncludedDataGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    row-gap: 12px;
    column-gap: 24px;
`

const IncludedDataOption = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
`
